/**
 * Graph API Test Page
 * 
 * Simple test page to verify Microsoft Graph API integration
 * Access at: http://localhost:3000/graph-test
 */

import React, { useState } from 'react';
import { UserProfileExample } from '../components/UserProfileExample';
import { BulkUserLookup } from '../components/BulkUserLookup';

type TabType = 'profile' | 'bulk-lookup' | 'status';

export const GraphAPITest: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('bulk-lookup');

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4 sm:px-6 lg:px-8 overflow-auto">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Microsoft Graph API Test
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Testing Graph API integration in Lab environment
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('bulk-lookup')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'bulk-lookup'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Bulk User Lookup
            </button>
            <button
              onClick={() => setActiveTab('profile')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'profile'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              My Profile
            </button>
            <button
              onClick={() => setActiveTab('status')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'status'
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Integration Status
            </button>
          </nav>
        </div>

        {/* Bulk User Lookup Tab */}
        {activeTab === 'bulk-lookup' && (
          <BulkUserLookup />
        )}

        {/* My Profile Tab */}
        {activeTab === 'profile' && (
          <UserProfileExample autoLoad={true} />
        )}

        {/* Status Tab */}
        {activeTab === 'status' && (
          <>
            {/* Status Info */}
            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Integration Status
              </h2>
              <div className="space-y-2">
                <div className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-sm text-gray-700">Backend Graph service created</span>
                </div>
                <div className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-sm text-gray-700">Frontend Graph client created</span>
                </div>
                <div className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-sm text-gray-700">React hook implemented</span>
                </div>
                <div className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-sm text-gray-700">OIDC scopes updated with Graph permissions</span>
                </div>
                <div className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-sm text-gray-700">IT granted Graph access to Lab environment</span>
                </div>
                <div className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-sm text-gray-700">Bulk user lookup by email</span>
                </div>
              </div>
            </div>

            {/* API Endpoints Info */}
            <div className="bg-white shadow rounded-lg p-6 mt-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Available API Endpoints
              </h2>
              <div className="space-y-2">
                <div className="text-sm">
                  <code className="bg-gray-100 px-2 py-1 rounded">GET /api/graph/me</code>
                  <span className="text-gray-600 ml-2">- Current user profile</span>
                </div>
                <div className="text-sm">
                  <code className="bg-gray-100 px-2 py-1 rounded">GET /api/graph/me/photo</code>
                  <span className="text-gray-600 ml-2">- Current user photo</span>
                </div>
                <div className="text-sm">
                  <code className="bg-gray-100 px-2 py-1 rounded">GET /api/graph/me/manager</code>
                  <span className="text-gray-600 ml-2">- Current user's manager</span>
                </div>
                <div className="text-sm">
                  <code className="bg-gray-100 px-2 py-1 rounded">GET /api/graph/me/groups</code>
                  <span className="text-gray-600 ml-2">- Current user's groups</span>
                </div>
                <div className="text-sm">
                  <code className="bg-gray-100 px-2 py-1 rounded">GET /users/{'{email}'}</code>
                  <span className="text-gray-600 ml-2">- Get user by email (Graph direct)</span>
                </div>
              </div>
            </div>

            {/* Documentation Links */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-6">
              <h3 className="text-sm font-semibold text-blue-900 mb-2">
                📚 Documentation
              </h3>
              <ul className="space-y-1 text-sm text-blue-800">
                <li>• <a href="/docs/MICROSOFT_GRAPH_QUICK_START.md" className="underline">Quick Start Guide</a></li>
                <li>• <a href="/docs/MICROSOFT_GRAPH_INTEGRATION.md" className="underline">Full Integration Guide</a></li>
                <li>• <a href="/docs/GRAPH_SETUP_COMPLETE.md" className="underline">Setup Complete Summary</a></li>
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default GraphAPITest;

